// @ts-strict-ignore
import { AppStoreType, LastCompletedRelease, ReleaseType } from '../../types';
import * as React from 'react';
import Modal from 'antd/lib/modal';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { antGrey } from '../styles/globalStyles';
import { displayConsoleNameForAppStoreType } from '../../helpers/displayHelpers';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Alert from 'antd/lib/alert';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';
import {
    getPausedPhasedReleaseWarning,
    getReleasePreviousVersionToAllUsersTooltip,
    isPhasedReleasePaused,
} from '@/helpers';
import { useSelector } from 'react-redux';
import {
    selectPendingManualBlockingChecklistItemsForStepRun,
    selectRelease,
    selectShouldWarnAboutNewerCommits,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { Link } from 'react-router-dom';
import { createStepRunTabRoute, ReleaseStepTabType } from '@/helpers/routeHelpers';

type AppStoreReleaseConfirmationModalProps = {
    orgId: string;
    appId: string;
    releaseId: string;
    stepRunId?: string;
    appStoreType: AppStoreType;
    displayableVersion: string;
    open: boolean;
    confirmLoading?: boolean;
    statusMessage?: string;
    lastCompletedRelease?: LastCompletedRelease;
    onClose: () => void;
    onConfirm: (args: { releasePreviousSubmissionToAllUsersFirst: boolean }) => void;
};

export const AppStoreReleaseConfirmationModal: React.FC<AppStoreReleaseConfirmationModalProps> = ({
    orgId,
    appId,
    releaseId,
    stepRunId,
    appStoreType,
    displayableVersion,
    open,
    confirmLoading,
    statusMessage,
    lastCompletedRelease,
    onClose,
    onConfirm,
}: AppStoreReleaseConfirmationModalProps) => {
    const [shouldReleasePreviousSubmissionToAllUsersFirst, setShouldReleasePreviousSubmissionToAllUsersFirst] =
        React.useState(false);

    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const shouldWarnAboutNewerCommits = useSelector((state: RootState) =>
        selectShouldWarnAboutNewerCommits(state, releaseId),
    );
    const pendingManualBlockingChecklistItems =
        useSelector((state: RootState) =>
            selectPendingManualBlockingChecklistItemsForStepRun(state, releaseId, stepRunId),
        ) || [];
    const isReleaseRollback = release.type === ReleaseType.Rollback;
    const isReleaseHotfix = release.type === ReleaseType.Hotfix;
    const lastCompletedVersion = lastCompletedRelease?.version;
    const lastPhasedReleasePercent = lastCompletedRelease?.phasedReleasePercent;
    const isLastCompletedReleaseFullyReleased = !lastPhasedReleasePercent || lastPhasedReleasePercent === 100;
    const shouldShowReleasePreviousVersionToAllOption =
        lastCompletedVersion && !isLastCompletedReleaseFullyReleased && !isReleaseRollback && !isReleaseHotfix;

    const releasePreviousVersionToAllUsersTooltip =
        lastPhasedReleasePercent ? getReleasePreviousVersionToAllUsersTooltip(lastPhasedReleasePercent) : '';

    const isLastCompletedReleasePaused =
        lastCompletedRelease?.phasedReleaseState ?
            isPhasedReleasePaused(lastCompletedRelease.phasedReleaseState)
        :   false;

    const pausedPhasedReleaseWarning =
        isLastCompletedReleasePaused && lastPhasedReleasePercent ?
            getPausedPhasedReleaseWarning(lastCompletedVersion, lastPhasedReleasePercent)
        :   null;

    const submitButtonText = React.useMemo(() => {
        if (!shouldReleasePreviousSubmissionToAllUsersFirst) {
            return 'Submit';
        }

        return `Accelerate ${lastCompletedVersion} to 100% and release ${displayableVersion}`;
    }, [shouldReleasePreviousSubmissionToAllUsersFirst]);

    const handleCheckboxChange = (event) => {
        setShouldReleasePreviousSubmissionToAllUsersFirst(event.target.checked);
    };

    return (
        <Modal
            title={`Release ${displayableVersion} update`}
            onCancel={onClose}
            footer={(_, { CancelBtn }) => (
                <Space>
                    <CancelBtn />
                    <Button
                        type="primary"
                        loading={confirmLoading}
                        onClick={() => {
                            onConfirm({
                                releasePreviousSubmissionToAllUsersFirst:
                                    shouldReleasePreviousSubmissionToAllUsersFirst,
                            });
                        }}
                    >
                        {submitButtonText}
                    </Button>
                </Space>
            )}
            open={open}
            centered
            width={650}
        >
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                }}
            >
                {statusMessage && <Alert closable={false} showIcon message={statusMessage} type="info" />}
                {shouldWarnAboutNewerCommits && (
                    <Alert
                        closable={false}
                        showIcon
                        message="There are commits on your release branch that are newer than your currently selected release candidate build."
                        type="warning"
                    />
                )}

                {pendingManualBlockingChecklistItems.length > 0 && (
                    <Alert
                        closable={false}
                        showIcon
                        message={
                            <div>
                                <div>{`${pendingManualBlockingChecklistItems.length} blocking checklist item${
                                    pendingManualBlockingChecklistItems.length > 1 ? 's are' : ' is'
                                } incomplete for this step:`}</div>
                                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                    {pendingManualBlockingChecklistItems.map((item) => (
                                        <li key={item.id}>
                                            <Link
                                                to={createStepRunTabRoute(
                                                    orgId,
                                                    appId,
                                                    release.version,
                                                    item.stepRunId,
                                                    ReleaseStepTabType.Checklist,
                                                )}
                                            >
                                                {item.title}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        }
                        type="warning"
                    />
                )}

                <Text style={{ fontSize: 14, color: antGrey.grey8 }}>
                    Are you sure you want to release <b>{displayableVersion}</b> to{' '}
                    <b>{displayConsoleNameForAppStoreType(appStoreType)}</b>?
                </Text>

                {shouldShowReleasePreviousVersionToAllOption && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <Checkbox onChange={handleCheckboxChange}>
                                Accelerate <b>{lastCompletedVersion}</b> to 100% of users before releasing new version
                            </Checkbox>
                            <TooltipInfoIcon tooltipContent={releasePreviousVersionToAllUsersTooltip} />
                        </div>

                        {shouldReleasePreviousSubmissionToAllUsersFirst && pausedPhasedReleaseWarning && (
                            <Alert showIcon type="warning" closable={false} message={pausedPhasedReleaseWarning} />
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};
